import { type DocumentNode } from 'graphql';

import { type ExtendedFetcher } from '../atlassian-client-types.ts';

/**
 * Fetch policy determines how the cache is used for a request.
 */
export type FetchPolicy =
  | 'cache-first'
  | 'network-only'
  | 'cache-only'
  | 'no-cache'
  | 'cache-and-network';

/**
 * Network status for tracking request state.
 */
export enum NetworkStatus {
  Loading = 1,
  SetVariables = 2,
  FetchMore = 3,
  Refetch = 4,
  Poll = 5,
  Ready = 6,
  Error = 7,
}

/**
 * GraphQL error shape from server responses.
 */
export interface GraphQLError {
  message: string;
  locations?: Array<{ line: number; column: number }>;
  path?: Array<string | number>;
  extensions?: Record<string, unknown>;
}

/**
 * Options for GraphQL query operations.
 */
export interface QueryOptions<TVariables extends object = Record<string, unknown>> {
  query: string | DocumentNode;
  variables?: TVariables;
  context?: RequestContext;
  fetchPolicy?: FetchPolicy;
}

/**
 * Result from a GraphQL query operation.
 * Compatible with Apollo's ApolloQueryResult.
 */
export interface QueryResult<T = unknown> {
  data: T;
  errors?: GraphQLError[];
  loading: boolean;
  networkStatus: NetworkStatus;
}

/**
 * Options for GraphQL mutation operations.
 */
export interface MutationOptions<TVariables extends object = Record<string, unknown>> {
  mutation: string | DocumentNode;
  variables?: TVariables;
  context?: RequestContext;
}

/**
 * Result from a GraphQL mutation operation.
 * Compatible with Apollo's FetchResult.
 */
export interface MutationResult<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}

/**
 * Context passed with each GraphQL request.
 */
export interface RequestContext {
  tenantBaseUrl?: string;
  headers?: Record<string, string>;
  fetchOptions?: RequestInit;
  signal?: AbortSignal;
}

/**
 * Configuration for creating a GraphQL client.
 */
export interface GraphQLClientConfig {
  endpoint: string;
  fetcher?: ExtendedFetcher;
  headers?: Record<string, string>;
  cache?: CacheConfig;
  retry?: RetryConfig;
}

/**
 * Configuration for the GraphQL cache.
 */
export interface CacheConfig {
  maxSize?: number;
  defaultTTL?: number;
  enabled?: boolean;
}

/**
 * Configuration for retry logic.
 */
export interface RetryConfig {
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
  jitter?: boolean;
}

/**
 * Cached response stored in the cache.
 */
export interface CachedResponse<T = unknown> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

/**
 * Request middleware function type.
 */
export type RequestMiddleware = (
  context: RequestContext,
) => RequestContext | Promise<RequestContext>;

/**
 * GraphQL request body shape.
 */
export interface GraphQLRequestBody {
  query: string;
  variables?: object;
  operationName?: string;
}

/**
 * GraphQL response body shape.
 */
export interface GraphQLResponseBody<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}
